import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { ArrowRight, Check, X } from "lucide-react";
import { formatPhoneNumber } from "@/lib/utils";

interface FMCSADataPreviewModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  currentData: any;
  fetchedData: any;
  isLoading?: boolean;
}

export default function FMCSADataPreviewModal({
  open,
  onClose,
  onConfirm,
  currentData,
  fetchedData,
  isLoading = false
}: FMCSADataPreviewModalProps) {
  
  const ComparisonRow = ({ label, currentValue, fetchedValue }: { label: string; currentValue: any; fetchedValue: any }) => {
    const hasChange = currentValue !== fetchedValue;
    const displayCurrent = currentValue || "Not set";
    const displayFetched = fetchedValue || "Not set";
    
    return (
      <div className={`py-3 ${hasChange ? 'bg-yellow-50' : ''}`}>
        <div className="text-sm font-medium text-gray-700 mb-2">{label}</div>
        <div className="flex items-center gap-3">
          <div className="flex-1 text-sm">
            <span className="text-gray-500">Current: </span>
            <span className={currentValue ? "font-medium" : "text-gray-400 italic"}>{displayCurrent}</span>
          </div>
          {hasChange && <ArrowRight className="w-4 h-4 text-yellow-600 flex-shrink-0" />}
          <div className="flex-1 text-sm">
            <span className="text-gray-500">FMCSA: </span>
            <span className={`font-medium ${hasChange ? 'text-green-700' : ''}`}>{displayFetched}</span>
          </div>
        </div>
      </div>
    );
  };

  const formatBoolean = (value: any) => {
    if (value === null || value === undefined) return null;
    return value ? "Yes" : "No";
  };

  const formatAuthorityStatus = (value: any) => {
    if (!value) return null;
    // Convert single letter codes to full words
    if (value === 'A') return 'Active';
    if (value === 'N') return 'None';
    if (value === 'P') return 'Pending';
    // If it's already a full word, return as-is
    return value;
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Review FMCSA Data</DialogTitle>
          <DialogDescription>
            Review the data fetched from FMCSA. Changes are highlighted in yellow. Click confirm to update the carrier with this data.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Basic Information */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Basic Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-1">
              <ComparisonRow label="Legal Name" currentValue={currentData?.legalName} fetchedValue={fetchedData?.legalName} />
              <Separator />
              <ComparisonRow label="DBA Name" currentValue={currentData?.dbaName} fetchedValue={fetchedData?.dbaName} />
              <Separator />
              <ComparisonRow 
                label="Docket Number" 
                currentValue={currentData?.docketNumber ? `${currentData.docketPrefix}-${currentData.docketNumber}` : null}
                fetchedValue={fetchedData?.docketNumber ? `${fetchedData.docketPrefix}-${fetchedData.docketNumber}` : null}
              />
            </CardContent>
          </Card>

          {/* Contact Information */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Contact Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-1">
              <ComparisonRow label="Email" currentValue={currentData?.email} fetchedValue={fetchedData?.email} />
              <Separator />
              <ComparisonRow label="Phone" currentValue={formatPhoneNumber(currentData?.phone)} fetchedValue={formatPhoneNumber(fetchedData?.phone)} />
              <Separator />
              <ComparisonRow label="Physical Address" currentValue={currentData?.address} fetchedValue={fetchedData?.address} />
              <Separator />
              <ComparisonRow label="Mailing Address" currentValue={currentData?.mailingAddress} fetchedValue={fetchedData?.mailingAddress} />
            </CardContent>
          </Card>

          {/* Authority Status */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Authority Status</CardTitle>
            </CardHeader>
            <CardContent className="space-y-1">
              <ComparisonRow label="Common Authority" currentValue={formatAuthorityStatus(currentData?.commonAuthorityStatus)} fetchedValue={formatAuthorityStatus(fetchedData?.commonAuthorityStatus)} />
              <Separator />
              <ComparisonRow label="Common Application Pending" currentValue={formatBoolean(currentData?.commonApplicationPending)} fetchedValue={formatBoolean(fetchedData?.commonApplicationPending)} />
              <Separator />
              <ComparisonRow label="Common Revocation Pending" currentValue={formatBoolean(currentData?.commonRevocationPending)} fetchedValue={formatBoolean(fetchedData?.commonRevocationPending)} />
              <Separator />
              <ComparisonRow label="Contract Authority" currentValue={formatAuthorityStatus(currentData?.contractAuthorityStatus)} fetchedValue={formatAuthorityStatus(fetchedData?.contractAuthorityStatus)} />
              <Separator />
              <ComparisonRow label="Contract Application Pending" currentValue={formatBoolean(currentData?.contractApplicationPending)} fetchedValue={formatBoolean(fetchedData?.contractApplicationPending)} />
              <Separator />
              <ComparisonRow label="Contract Revocation Pending" currentValue={formatBoolean(currentData?.contractRevocationPending)} fetchedValue={formatBoolean(fetchedData?.contractRevocationPending)} />
              <Separator />
              <ComparisonRow label="Broker Authority" currentValue={formatAuthorityStatus(currentData?.brokerAuthorityStatus)} fetchedValue={formatAuthorityStatus(fetchedData?.brokerAuthorityStatus)} />
              <Separator />
              <ComparisonRow label="Broker Application Pending" currentValue={formatBoolean(currentData?.brokerApplicationPending)} fetchedValue={formatBoolean(fetchedData?.brokerApplicationPending)} />
              <Separator />
              <ComparisonRow label="Broker Revocation Pending" currentValue={formatBoolean(currentData?.brokerRevocationPending)} fetchedValue={formatBoolean(fetchedData?.brokerRevocationPending)} />
            </CardContent>
          </Card>

          {/* Operating Authority */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Operating Authority</CardTitle>
            </CardHeader>
            <CardContent className="space-y-1">
              <ComparisonRow label="Property Carriage" currentValue={formatBoolean(currentData?.propertyCarriage)} fetchedValue={formatBoolean(fetchedData?.propertyCarriage)} />
              <Separator />
              <ComparisonRow label="Passenger Carriage" currentValue={formatBoolean(currentData?.passengerCarriage)} fetchedValue={formatBoolean(fetchedData?.passengerCarriage)} />
              <Separator />
              <ComparisonRow label="Household Goods" currentValue={formatBoolean(currentData?.householdGoodsCarriage)} fetchedValue={formatBoolean(fetchedData?.householdGoodsCarriage)} />
              <Separator />
              <ComparisonRow label="Private Carriage" currentValue={formatBoolean(currentData?.privateCarriage)} fetchedValue={formatBoolean(fetchedData?.privateCarriage)} />
            </CardContent>
          </Card>

          {/* Insurance */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Insurance & Bond</CardTitle>
            </CardHeader>
            <CardContent className="space-y-1">
              <ComparisonRow label="BIPD Required" currentValue={currentData?.bipdRequired} fetchedValue={fetchedData?.bipdRequired} />
              <Separator />
              <ComparisonRow label="BIPD On File" currentValue={currentData?.bipdOnFile} fetchedValue={fetchedData?.bipdOnFile} />
              <Separator />
              <ComparisonRow label="Cargo Required" currentValue={currentData?.cargoRequired} fetchedValue={fetchedData?.cargoRequired} />
              <Separator />
              <ComparisonRow label="Cargo On File" currentValue={currentData?.cargoOnFile} fetchedValue={fetchedData?.cargoOnFile} />
              <Separator />
              <ComparisonRow label="Bond Required" currentValue={currentData?.bondRequired} fetchedValue={fetchedData?.bondRequired} />
              <Separator />
              <ComparisonRow label="Bond On File" currentValue={currentData?.bondOnFile} fetchedValue={fetchedData?.bondOnFile} />
            </CardContent>
          </Card>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-2">
              <div className="text-blue-600 mt-0.5">ℹ️</div>
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-1">Important:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>This will update the carrier record with data from FMCSA</li>
                  <li>Fields highlighted in yellow will be changed</li>
                  <li>Empty FMCSA fields will clear existing data</li>
                  <li>You can edit the carrier manually after importing if needed</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            <X className="w-4 h-4 mr-2" />
            Cancel
          </Button>
          <Button onClick={onConfirm} disabled={isLoading} data-testid="button-confirm-fmcsa-update">
            <Check className="w-4 h-4 mr-2" />
            {isLoading ? "Updating..." : "Confirm & Update"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
